import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const EquipmentDowntime = () => {
  // State variables for Equipment Downtime calculation
  const [totalDowntimeHours, setTotalDowntimeHours] = useState(0); // Total downtime hours
  const [totalOperatingHours, setTotalOperatingHours] = useState(0); // Total operating hours
  const [downtimePercentage, setDowntimePercentage] = useState(0); // Calculated downtime percentage

  // Event handlers to update state values
  const handleTotalDowntimeHoursChange = (e) => setTotalDowntimeHours(parseFloat(e.target.value) || 0);
  const handleTotalOperatingHoursChange = (e) => setTotalOperatingHours(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate downtime percentage when any input changes
  useEffect(() => {

    if (totalOperatingHours > 0) {
      const downtime = (totalDowntimeHours / totalOperatingHours) * 100;
      setDowntimePercentage(downtime);
    } else {
      setDowntimePercentage(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [totalDowntimeHours, totalOperatingHours]);

  return (
    <Layout title="Equipment Downtime Calculator" path="equipment-downtime">
      <section className="section-padding" id="equipment-downtime-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Equipment Downtime Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Equipment Downtime Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Downtime Hours Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalDowntimeHours" className="form-label">
                        Total Downtime Hours
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Downtime Hours:</strong> This refers to the total number of hours your equipment was non-operational or out of service."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalDowntimeHours"
                        placeholder="Enter total downtime hours"
                        value={totalDowntimeHours || ""}
                        onChange={handleTotalDowntimeHoursChange}
                      />
                    </div>

                    {/* Total Operating Hours Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalOperatingHours" className="form-label">
                        Total Operating Hours
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Operating Hours:</strong> This refers to the total number of hours your equipment was running or operational during the same period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalOperatingHours"
                        placeholder="Enter total operating hours"
                        value={totalOperatingHours || ""}
                        onChange={handleTotalOperatingHoursChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Equipment Downtime Percentage:{" "}
                        <span className="fw-bold ms-3">
                          {downtimePercentage === 0 ? "0.00" : downtimePercentage.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Equipment Downtime Calculator
                </h3>

                {/* What is an Equipment Downtime Calculator? */}
                <h4 className="mt-3 mb-3">What is an Equipment Downtime Calculator?</h4>
                <p>
                  An **Equipment Downtime Calculator** helps measure the percentage of time equipment is unavailable for use due to breakdowns, maintenance, or other issues. It calculates downtime as a percentage of total operating hours, allowing businesses to assess how efficiently their equipment is being utilized.
                </p>

                {/* Why Use an Equipment Downtime Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Equipment Downtime Calculator?</h4>
                <p>
                  This calculator helps identify the extent of downtime in equipment operations, which is crucial for improving maintenance schedules, reducing delays, and optimizing operational efficiency.
                </p>

                {/* Benefits of Using an Equipment Downtime Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Equipment Downtime Calculator</h4>
                <ul>
                  <li><strong>Improves Equipment Maintenance Practices:</strong> Identifies when equipment downtime is excessive, encouraging better maintenance scheduling and practices.</li>
                  <li><strong>Reduces Production Delays:</strong> Helps pinpoint sources of delay, enabling the implementation of strategies to minimize downtime.</li>
                  <li><strong>Supports Operational Efficiency:</strong> By reducing downtime, it improves overall productivity and profitability.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Equipment Downtime Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Manufacturers:</strong> Helps identify inefficiencies in equipment use and improve production schedules.</li>
                  <li><strong>Construction Companies:</strong> Ensures heavy machinery is optimally utilized, reducing downtime during projects.</li>
                  <li><strong>Facilities Management Teams:</strong> Enables efficient management of equipment across buildings or facilities to maintain service levels.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How can I reduce equipment downtime?</h4>
                <p>
                  Reducing downtime can be achieved through regular maintenance, upgrading outdated equipment, and implementing predictive maintenance tools to anticipate breakdowns before they occur.
                </p>

                <h4 className="mt-4 mb-3">What is considered a good equipment downtime percentage?</h4>
                <p>
                  A good equipment downtime percentage depends on the industry and the type of equipment. However, most businesses aim for a downtime percentage of less than 10%. Anything higher could indicate operational inefficiencies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EquipmentDowntime;
